import { type ServingBinding, toolKey, toolsServing } from '@/lib/teammateTools'

/**
 * What "Add a teammate" (#1758) hands on about a teammate it has just made, kept in this browser tab under the
 * teammate's id: the tools their tasks need that the company had not connected, and a described job's first task.
 * The teammate's page lists the tools still unconnected, each with Connect; the Agents page, where "its own account"
 * lands, offers the first task next, so choosing an own account never loses it. Nothing here is sent anywhere.
 */
const PREFIX = 'zh.teammate-add.'

/** One tool the teammate's tasks need, and the names of the tasks that use it (its reason). */
export type HandedTool = { tool: string; tasks: string[] }

/** A described job's first task, as Create task takes it: the sentence, the job's role id and the team id. */
export type HandedFirstTask = { goal: string; role: string; team: string }

export type TeammateHandoff = { tools: HandedTool[]; firstTask: HandedFirstTask | null }

type TabStore = Pick<Storage, 'getItem' | 'setItem'>

/** The tab's own storage, or null where there is none (a private window that refuses it). */
export function tabStorage(): Storage | null {
    try {
        return globalThis.sessionStorage ?? null
    } catch {
        return null
    }
}

const text = (value: unknown): string => (typeof value === 'string' ? value.trim() : '')

function handedTools(value: unknown): HandedTool[] {
    if (!Array.isArray(value)) return []
    return value.flatMap(row => {
        const { tool, tasks } = (row ?? {}) as Record<string, unknown>
        const named = text(tool)
        if (!named) return []
        const users = Array.isArray(tasks) ? tasks.map(text).filter(Boolean) : []
        return [{ tool: named, tasks: users }]
    })
}

function handedFirstTask(value: unknown): HandedFirstTask | null {
    const { goal, role, team } = (value ?? {}) as Record<string, unknown>
    const kept = { goal: text(goal), role: text(role), team: text(team) }
    return kept.goal ? kept : null
}

/** Keeps the hand-off for `memberId`, replacing any earlier one. Adding a teammate still works without storage. */
export function keepTeammateHandoff(
    memberId: string,
    handoff: TeammateHandoff,
    storage: TabStore | null = tabStorage(),
): void {
    if (!memberId) return
    try {
        storage?.setItem(`${PREFIX}${memberId}`, JSON.stringify(handoff))
    } catch {
        // A full or refused storage only loses the reminder; the teammate is made either way.
    }
}

/** The hand-off kept for `memberId` in this tab, or null when there is none or it cannot be read. */
export function teammateHandoff(memberId: string, storage: TabStore | null = tabStorage()): TeammateHandoff | null {
    if (!memberId) return null
    try {
        const raw = storage?.getItem(`${PREFIX}${memberId}`)
        if (!raw) return null
        const saved = JSON.parse(raw) as Record<string, unknown>
        return { tools: handedTools(saved['tools']), firstTask: handedFirstTask(saved['firstTask']) }
    } catch {
        return null
    }
}

/**
 * What a teammate's page says once when a step after the teammate existed did not happen (#2128): who was added, each
 * step missed in plain words, and the next steps still waiting, so nothing is lost and nothing is sent twice.
 */
export type TeammateNotice = {
    /** The teammate's name as it was typed, or empty. */
    name: string
    /** Each step that did not happen, as the sentence names it: "save their photo", "add them to Finance". */
    missed: string[]
    /** Whether the daily cap was one of them: it is set on Spend, not on the teammate's page. */
    capMissed: boolean
    /** Whether their own AI account was chosen, so connecting it is still to do. */
    connectOwn: boolean
    /** A described job's first task, still to finish on Create task. */
    firstTask: HandedFirstTask | null
}

type NoticeStore = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>

const NOTICE_PREFIX = 'zh.teammate-add-notice.'

/** Keeps the notice for `memberId` in this tab, replacing any earlier one. Without storage there is no notice. */
export function keepTeammateNotice(
    memberId: string,
    notice: TeammateNotice,
    storage: NoticeStore | null = tabStorage(),
): void {
    if (!memberId || notice.missed.length === 0) return
    try {
        storage?.setItem(`${NOTICE_PREFIX}${memberId}`, JSON.stringify(notice))
    } catch {
        // A full or refused storage only loses the notice; the teammate and every step that worked are kept.
    }
}

/**
 * The notice kept for `memberId`, taken out of the tab so it shows once: opening the page again shows nothing. Null
 * when there is none, it names no missed step, or it cannot be read.
 */
export function takeTeammateNotice(
    memberId: string,
    storage: NoticeStore | null = tabStorage(),
): TeammateNotice | null {
    if (!memberId) return null
    try {
        const key = `${NOTICE_PREFIX}${memberId}`
        const raw = storage?.getItem(key)
        if (!raw) return null
        storage?.removeItem(key)
        const saved = JSON.parse(raw) as Record<string, unknown>
        const missed = Array.isArray(saved['missed']) ? saved['missed'].map(text).filter(Boolean) : []
        if (!missed.length) return null
        return {
            name: text(saved['name']),
            missed,
            capMissed: saved['capMissed'] === true,
            connectOwn: saved['connectOwn'] === true,
            firstTask: handedFirstTask(saved['firstTask']),
        }
    } catch {
        return null
    }
}

/** Words joined as a sentence lists them: "a", "a or b", "a, b or c". */
function listOr(words: readonly string[]): string {
    if (words.length <= 1) return words[0] ?? ''
    return `${words.slice(0, -1).join(', ')} or ${words[words.length - 1]}`
}

/** Where each missed step can be done again: this page for everything but the daily cap, which Spend sets. */
function whereToFinish(notice: TeammateNotice): string {
    const onThisPage = notice.missed.length > (notice.capMissed ? 1 : 0)
    if (!notice.capMissed) return 'You can do that here.'
    return onThisPage ? 'You can do that here, and set the cap on Spend.' : 'You can set it on Spend.'
}

/**
 * The notice in plain words: that the teammate was added, what did not happen and where to do it, as in "Casey was
 * added. We couldn't save their photo or add them to Finance. You can do that here."
 */
export function noticeSentence(notice: TeammateNotice): string {
    const who = notice.name || 'Your teammate'
    return `${who} was added. We couldn't ${listOr(notice.missed)}. ${whereToFinish(notice)}`
}

export type { ServingBinding } from '@/lib/teammateTools'

/**
 * The handed tools a teammate still has no connection for, as the role review counts one (by tool type, any case): at
 * company level, on one of `teamIds`, or on the teammate's own layer. The serving rule is the one the teammate's card
 * on Members draws its Tools field with (`toolsServing`).
 */
export function stillToConnect(
    tools: readonly HandedTool[],
    bindings: readonly ServingBinding[],
    memberId: string,
    teamIds: readonly string[],
): HandedTool[] {
    const connected = new Set(toolsServing(bindings, memberId, teamIds).map(binding => toolKey(binding.toolType)))
    return tools.filter(need => !connected.has(toolKey(need.tool)))
}
